import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { toast } from 'sonner';
import { Users, Mail, Phone, Pencil, Trash2, MoreVertical } from 'lucide-react';
import type { Client, ClientCreateInput, ClientUpdateInput } from 'shared/schemas';
import { Avatar, AvatarFallback } from '../../../components/ui/avatar.tsx';
import { Badge } from '../../../components/ui/badge.tsx';
import { Button } from '../../../components/ui/button.tsx';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog.tsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu.tsx';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../../../components/ui/sheet.tsx';
import { FilterBar } from '../../../components/layout/FilterBar.tsx';
import { GradientCard } from '../../../components/layout/GradientCard.tsx';
import { KpiCard } from '../../../features/dashboard/components/KpiCard.tsx';
import { KpiStrip } from '../../../features/dashboard/components/KpiStrip.tsx';
import { EmptyStateV3 } from '../../../components/layout/EmptyStateV3.tsx';
import { PageHeader } from '../../../components/layout/PageHeader.tsx';
import { SectionHeader } from '../../../components/layout/SectionHeader.tsx';
import { ClientDeleteDialog } from '../../../features/clients/components/ClientDeleteDialog.tsx';
import { ClientForm } from '../../../features/clients/components/ClientForm.tsx';
import { useClients } from '../../../features/clients/hooks/useClients.ts';
import { useClientsStats } from '../../../features/clients/hooks/useClientsStats.ts';
import { useCreateClient } from '../../../features/clients/hooks/useCreateClient.ts';
import { useUpdateClient } from '../../../features/clients/hooks/useUpdateClient.ts';
import { STRINGS } from '../../../features/clients/clients.strings.ts';

const clientsSearchSchema = z.object({
  view: z.string().optional(),
  edit: z.string().optional(),
  new: z.boolean().optional(),
});

export const Route = createFileRoute('/_authed/clients/')({
  validateSearch: (s) => clientsSearchSchema.parse(s),
  component: ClientsPage,
});

function getInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

function ClientsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('todos');
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null);

  const { view: selectedId, new: isNewOpen, edit: editId } = Route.useSearch();

  const { data, isLoading } = useClients({ page: 1, search: search || undefined });
  const stats = useClientsStats();
  const createClient = useCreateClient();
  const updateClient = useUpdateClient(editId ?? '');

  const tabs = [
    { id: 'todos', label: 'Todos', count: stats.total },
    { id: 'con-email', label: 'Con email', count: stats.withEmail },
    { id: 'sin-email', label: 'Sin email', count: stats.total - stats.withEmail },
  ];

  const clients = data?.data ?? [];

  // Filter by tab client-side (API doesn't filter by email presence)
  const filteredClients = clients.filter((c) => {
    if (activeTab === 'con-email') return c.email !== null;
    if (activeTab === 'sin-email') return c.email === null;
    return true;
  });

  const selectedClient = clients.find((c) => c.id === selectedId) ?? null;
  const editClient = editId ? clients.find((c) => c.id === editId) : null;

  function openNew() {
    void navigate({ to: '/clients', search: { new: true }, replace: true });
  }

  function closeModals() {
    void navigate({ to: '/clients', search: {}, replace: true });
  }

  function selectClient(client: Client) {
    void navigate({ to: '/clients', search: { view: client.id }, replace: true });
  }

  function handleCreate(data: ClientCreateInput) {
    createClient.mutate(data, {
      onSuccess: () => {
        toast.success(STRINGS.TOAST_CREATE_SUCCESS);
        closeModals();
      },
      onError: () => {
        toast.error(STRINGS.TOAST_ERROR_GENERIC);
      },
    });
  }

  function handleUpdate(data: ClientUpdateInput) {
    updateClient.mutate(data, {
      onSuccess: () => {
        toast.success(STRINGS.TOAST_UPDATE_SUCCESS);
        closeModals();
      },
      onError: () => {
        toast.error(STRINGS.TOAST_ERROR_GENERIC);
      },
    });
  }

  return (
    <div className="flex-1 overflow-auto px-3 sm:px-6 py-4 sm:py-6">
      <PageHeader
        title={STRINGS.LIST_TITLE}
        actions={
          <Button type="button" onClick={openNew}>
            {STRINGS.NEW_BUTTON}
          </Button>
        }
      />

      <KpiStrip>
        <KpiCard label="Total" value={String(stats.total)} />
        <KpiCard label="Con email" value={String(stats.withEmail)} />
        <KpiCard label="Con teléfono" value={String(stats.withPhone)} />
        <KpiCard label="Nuevos este mes" value={String(stats.newThisMonth)} />
      </KpiStrip>

      <div className="mt-6">
        <SectionHeader title="Lista de clientes" />

        <FilterBar
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={(id) => setActiveTab(id)}
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder={STRINGS.PLACEHOLDER_SEARCH}
        />

        {isLoading ? (
          <div className="mt-4 text-sm text-muted-foreground">Cargando...</div>
        ) : filteredClients.length === 0 ? (
          <div className="mt-8">
            <EmptyStateV3
              icon={Users}
              title="Sin clientes"
              description={search ? STRINGS.NO_RESULTS : STRINGS.EMPTY_STATE}
              action={
                <Button type="button" onClick={openNew}>
                  {STRINGS.NEW_BUTTON}
                </Button>
              }
            />
          </div>
        ) : (
          <div
            data-testid="clients-grid"
            className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3"
          >
            {filteredClients.map((client) => (
              <ClientCard
                key={client.id}
                client={client}
                onClick={() => selectClient(client)}
                onEdit={() =>
                  void navigate({ to: '/clients', search: { edit: client.id }, replace: true })
                }
                onDelete={() => setClientToDelete(client)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Client detail sheet */}
      <Sheet
        open={selectedClient !== null}
        onOpenChange={(open) => { if (!open) closeModals(); }}
      >
        <SheetContent side="right" className="w-full max-w-md overflow-y-auto p-0">
          {selectedClient && (
            <>
              <SheetHeader className="border-b border-border p-6 pb-4">
                <div className="flex items-center gap-3">
                  <Avatar className="size-12 shrink-0">
                    <AvatarFallback className="text-sm font-semibold">
                      {getInitials(selectedClient.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <SheetTitle className="truncate text-left">
                      {selectedClient.name}
                    </SheetTitle>
                    {selectedClient.document && (
                      <p className="text-xs text-muted-foreground">{selectedClient.document}</p>
                    )}
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        void navigate({ to: '/clients', search: { edit: selectedClient.id }, replace: true })
                      }
                    >
                      <Pencil className="size-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => setClientToDelete(selectedClient)}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </div>
                </div>
              </SheetHeader>

              <div className="p-6">
                {/* Contact info */}
                <div className="flex flex-col gap-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {STRINGS.SECTION_CONTACT}
                  </p>
                  {selectedClient.email ? (
                    <a
                      href={`mailto:${selectedClient.email}`}
                      className="flex items-center gap-2 text-sm text-primary hover:underline"
                    >
                      <Mail className="size-4" />
                      {selectedClient.email}
                    </a>
                  ) : (
                    <p className="text-sm text-muted-foreground">Sin email.</p>
                  )}
                  {selectedClient.phone && (
                    <span className="flex items-center gap-2 text-sm">
                      <Phone className="size-4 text-muted-foreground" />
                      {selectedClient.phone}
                    </span>
                  )}
                </div>

                {/* Notes */}
                {selectedClient.notes && (
                  <div className="mt-4 flex flex-col gap-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {STRINGS.SECTION_NOTES}
                    </p>
                    <p className="whitespace-pre-line text-sm">{selectedClient.notes}</p>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* New client dialog */}
      <Dialog
        open={isNewOpen === true}
        onOpenChange={(open) => { if (!open) closeModals(); }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{STRINGS.NEW_BUTTON}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <ClientForm
              mode="create"
              formId="client-create-form"
              onSubmit={handleCreate}
              isPending={createClient.isPending}
            />
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeModals}>
              {STRINGS.ACTION_CANCEL}
            </Button>
            <Button type="submit" form="client-create-form" disabled={createClient.isPending}>
              {STRINGS.ACTION_SAVE}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit client dialog */}
      <Dialog
        open={editClient !== null && editClient !== undefined}
        onOpenChange={(open) => { if (!open) closeModals(); }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{STRINGS.EDIT_TITLE}</DialogTitle>
          </DialogHeader>
          {editClient && (
            <>
              <DialogBody>
                <ClientForm
                  mode="edit"
                  formId="client-edit-form"
                  initialValues={editClient}
                  onSubmit={handleUpdate}
                  isPending={updateClient.isPending}
                />
              </DialogBody>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={closeModals}>
                  {STRINGS.ACTION_CANCEL}
                </Button>
                <Button type="submit" form="client-edit-form" disabled={updateClient.isPending}>
                  {STRINGS.ACTION_SAVE}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <ClientDeleteDialog
        client={clientToDelete}
        open={clientToDelete !== null}
        onClose={() => setClientToDelete(null)}
      />
    </div>
  );
}

interface ClientCardProps {
  client: Client;
  onClick: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

function ClientCard({ client, onClick, onEdit, onDelete }: ClientCardProps) {
  return (
    <GradientCard className="flex flex-col gap-3 p-4">
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <button type="button" onClick={onClick} className="shrink-0">
          <Avatar className="size-10">
            <AvatarFallback className="text-sm font-medium">
              {getInitials(client.name)}
            </AvatarFallback>
          </Avatar>
        </button>

        {/* Content */}
        <button
          type="button"
          onClick={onClick}
          className="min-w-0 flex-1 text-left"
        >
          <p className="truncate text-sm font-semibold">{client.name}</p>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            {client.email ?? client.phone ?? 'Sin contacto'}
          </p>
        </button>

        {/* Actions menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="ghost" size="icon" className="size-8 shrink-0">
              <MoreVertical className="size-4" />
              <span className="sr-only">Opciones</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onEdit}>
              <Pencil className="mr-2 size-4" />
              {STRINGS.ACTION_EDIT}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={onDelete}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 size-4" />
              {STRINGS.ACTION_DELETE}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Contact icons row */}
      <div className="flex items-center gap-2">
        {client.email && (
          <Badge variant="secondary" className="gap-1 text-xs">
            <Mail className="size-3" />
            Email
          </Badge>
        )}
        {client.phone && (
          <Badge variant="secondary" className="gap-1 text-xs">
            <Phone className="size-3" />
            Tel
          </Badge>
        )}
      </div>
    </GradientCard>
  );
}
